import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import { FaLocationDot } from "react-icons/fa6";
import { BiSolidPhoneCall } from "react-icons/bi";
import { IoMailSharp } from "react-icons/io5";
import { Check, X } from "lucide-react";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Constant from "../utils/Constant";
import { Link } from "react-router-dom";

// ✅ Enhanced LoadingSpinner Component
const LoadingSpinner = () => {
  return (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingSpinner}>
        <div style={{...styles.spinnerRing, ...styles.ring1}}></div>
        <div style={{...styles.spinnerRing, ...styles.ring2}}></div>
        <div style={{...styles.spinnerRing, ...styles.ring3}}></div>
        <div style={styles.centerText}>Ziegler</div>
      </div>
      <div style={styles.loadingText}>Loading contact information...</div>
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '2rem',
    textAlign: 'center',
    opacity: 0,
    animation: 'fadeIn 0.4s ease-out forwards'
  },
  loadingSpinner: {
    position: 'relative',
    width: '120px',
    height: '120px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  spinnerRing: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    border: '4px solid transparent',
    borderTop: '4px solid #099CD6',
    borderRadius: '50%',
    animation: 'spin 1.2s linear infinite'
  },
  ring2: {
    width: '80%',
    height: '80%',
    top: '10%',
    left: '10%',
    borderTop: '4px solid #66b3e0',
    animationDelay: '-0.4s'
  },
  ring3: {
    width: '60%',
    height: '60%',
    top: '20%',
    left: '20%',
    borderTop: '4px solid #99cceb',
    animationDelay: '-0.8s'
  },
  centerText: {
    position: 'relative',
    zIndex: 10,
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#099CD6'
  },
  loadingText: {
    opacity: 0,
    animation: 'fadeIn 0.6s ease-out 0.3s forwards',
    marginTop: '20px',
    fontSize: '18px',
    color: '#666'
  }
};

const ContactUs = () => {
  const [contactData, setContactData] = useState(null);
  const [contact_us_subject, setContact_us_subject] = useState("");
  const [showConsentModal, setShowConsentModal] = useState(false);
  
  const [consentCheckboxes, setConsentCheckboxes] = useState({
    commercial: false,
    marketing: false,
    crm: false,
    partnerZone: false
  });
  
  const subjectOptions = [
    "General Inquiry",
    "Get a Quote",
    "Request for custom product",
    "Want to Become a Partner or Reseller"
  ];

  const [formData, setFormData] = useState({
    name: "",
    country: "",
    company: "",
    designation: "",
    email: "",
    phone_number: "",
    contact_us_message: ""
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isLoadingContact, setIsLoadingContact] = useState(true);
  const [minLoadingTime, setMinLoadingTime] = useState(true);

  useEffect(() => {
    fetchContactData();
    
    const minLoadingTimer = setTimeout(() => {
      setMinLoadingTime(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
  }, []);

const fetchContactData = async () => {
  try {
    const response = await fetch(
      `${Constant.BASE_URL}/api/v1/profile?ts=${Date.now()}`, // cache-busting param
      {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );

    const result = await response.json();
    if (result.status && result.data?.length > 0) {
      setContactData(result.data[0]);
    }
  } catch (error) {
    toast.error("Network error. Please check your connection and try again.");
  } finally {
    setIsLoadingContact(false);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleConsentChange = (checkboxName) => {
    setConsentCheckboxes(prev => ({
      ...prev,
      [checkboxName]: !prev[checkboxName]
    }));
  };

  // ✅ MODIFIED: Enhanced form validation to prevent XSS
  const validateForm = () => {
    const newErrors = {};
    
    // ✅ ADDED: Regex to detect forbidden characters
    const forbiddenCharsRegex = /[<>"'&/]/;
    
    // Required fields validation
    const requiredFields = [
      { field: 'name', label: 'Full Name' },
      { field: 'country', label: 'Country' },
      { field: 'company', label: 'Company' },
      { field: 'designation', label: 'Designation' },
      { field: 'email', label: 'Email' },
      { field: 'contact_us_message', label: 'Message' }
    ];

    requiredFields.forEach(({ field, label }) => {
      if (!formData[field] || formData[field].trim() === '') {
        newErrors[field] = `${label} is required`;
      }
    });
    
    // ✅ ADDED: Loop to check free-text fields for malicious characters
    const fieldsToSanitize = [
        { field: 'name', label: 'Full Name' },
        { field: 'country', label: 'Country' },
        { field: 'company', label: 'Company' },
        { field: 'designation', label: 'Designation' },
        { field: 'contact_us_message', label: 'Message' }
    ];

    fieldsToSanitize.forEach(({ field, label }) => {
        if (formData[field] && forbiddenCharsRegex.test(formData[field])) {
            newErrors[field] = `${label} contains invalid characters`;
        }
    });

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone number validation (optional but format check if provided)
    if (formData.phone_number && formData.phone_number.trim() !== '') {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(formData.phone_number.replace(/[-\s]/g, ''))) {
        newErrors.phone_number = 'Please enter a valid phone number with country code (e.g., +91-9876543210)';
      }
    }

    // Subject validation
    if (!contact_us_subject) {
      newErrors.contact_us_subject = 'Please select a subject';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the form errors before proceeding");
      return;
    }
    setShowConsentModal(true);
  };

  const handleConsentSubmit = async () => {
    const allConsentsGiven = Object.values(consentCheckboxes).every(consent => consent === true);
    if (!allConsentsGiven) {
      toast.error("Please accept all consent terms to proceed");
      return;
    }

    setIsSubmitting(true);
    
    const submissionData = {
      name: formData.name.trim(),
      country: formData.country.trim(),
      company: formData.company.trim(),
      designation: formData.designation.trim(),
      email: formData.email.trim(),
      phone_number: formData.phone_number.trim() || null,
      contact_us_subject: contact_us_subject,
      contact_us_message: formData.contact_us_message.trim(),
      consents: consentCheckboxes
    };

    try {
      const response = await fetch(`${Constant.BASE_URL}/api/v1/contact-us`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });
      const result = await response.json();

      if (result.status === false) {
        if (result.errors) {
          const errorMessages = {};
          Object.keys(result.errors).forEach(key => {
            errorMessages[key] = result.errors[key][0];
          });
          setErrors(errorMessages);
          toast.error("Network error. Please check your connection and try again.");

        } else {
          toast.error("Network error. Please check your connection and try again.");
        }
      } else {
        toast.success("Your message has been sent successfully! We'll get back to you soon.");
        
        // Reset form
        setFormData({
          name: "",
          country: "",
          company: "",
          designation: "",
          email: "",
          phone_number: "",
          contact_us_message: ""
        });
        setContact_us_subject("");
        setErrors({});
        setConsentCheckboxes({
          commercial: false,
          marketing: false,
          crm: false,
          partnerZone: false
        });
        setShowConsentModal(false);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const shouldShowLoading = isLoadingContact || minLoadingTime;

  return (
    <>
      <Navbar />
      {shouldShowLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="w-full max-w-6xl 2xl:max-w-7xl mx-auto p-4 md:p-8 lg:p-0 my-4 md:my-8 lg:my-12">
          <h1 className="text-2xl md:text-[40px] 2xl:text-[50px] font-poppins font-bold text-center mb-2 3xl:mb-4">
            Contact Us
          </h1>
          <p className="text-center font-medium font-poppins text-base md:text-lg 2xl:text-2xl text-[#717171] mb-8">
            Any question or remarks? Just write us a message!
          </p>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              {/* Left Section - Contact Information */}
              <div
                className="lg:w-[40%] p-6 md:p-8 lg:p-10 text-white"
                style={{
                  background: "linear-gradient(135deg, #7DCFB6 0%, #099CD6 100%)",
                }}
              >
                <div className="max-w-[360px] 2xl:max-w-[480px] mx-auto">
                  <h2 className="text-xl md:text-2xl 2xl:text-3xl font-semibold mb-4">
                    Contact Information
                  </h2>
                  <p className="text-[#FAFAFA] opacity-80 text-base md:text-lg 2xl:text-xl mb-8">
                    Say something to start a live chat!
                  </p>

                  <div className="space-y-6 md:space-y-8 2xl:space-y-10">
                    <div className="flex items-center gap-4">
                      <BiSolidPhoneCall className="w-6 h-6 md:w-8 md:h-8 2xl:w-10 2xl:h-10" />
                      <p className="text-base md:text-lg 2xl:text-xl">
                        {contactData?.profile_mobile || 'Loading...'}
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      <IoMailSharp className="w-6 h-6 md:w-8 md:h-8 2xl:w-10 2xl:h-10" />
                      <p className="text-base md:text-lg 2xl:text-xl break-all">
                        {contactData?.profile_email || 'Loading...'}
                      </p>
                    </div>

                    <div className="flex gap-4">
                      <FaLocationDot className="w-6 h-6 md:w-8 md:h-8 2xl:w-10 2xl:h-10 flex-shrink-0 mt-1" />
                      <div className="text-base md:text-lg 2xl:text-xl space-y-3">
                        {contactData?.profile_address_a && (
                          <div>{contactData.profile_address_a}</div>
                        )}
                        {contactData?.profile_address_b && (
                          <div>{contactData.profile_address_b}</div>
                        )}
                        {!contactData?.profile_address_a && !contactData?.profile_address_b && (
                          <div>Loading address...</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Section - Contact Form */}
              <div className="lg:w-[60%] p-6 md:p-8 lg:p-10">
                <form onSubmit={handleFormSubmit} className="max-w-[600px] 2xl:max-w-[800px] mx-auto">
                  
                  {/* Name Field */}
                  <div className="mb-6">
                    <label className="block text-sm md:text-base 2xl:text-lg text-black mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full border-b border-gray-300 pb-2 focus:outline-none focus:border-blue-500 text-base md:text-lg 2xl:text-xl transition-colors ${
                        errors.name ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter your full name"
                      maxLength={100}
                      autoComplete="off"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  {/* Country and Company Row */}
                  <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-6">
                    <div>
                      <label className="block text-sm md:text-base 2xl:text-lg text-black mb-2">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className={`w-full border-b border-gray-300 pb-2 focus:outline-none focus:border-blue-500 text-base md:text-lg 2xl:text-xl transition-colors ${
                          errors.country ? 'border-red-500' : ''
                        }`}
                        placeholder="Enter your country"
                        maxLength={100}
                        autoComplete="on"
                      />
                      {errors.country && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.country}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm md:text-base 2xl:text-lg text-black mb-2">
                        Company <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={`w-full border-b border-gray-300 pb-2 focus:outline-none focus:border-blue-500 text-base md:text-lg 2xl:text-xl transition-colors ${
                          errors.company ? 'border-red-500' : ''
                        }`}
                        placeholder="Enter your company name"
                        maxLength={100}
                        autoComplete="on"
                      />
                      {errors.company && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.company}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Designation and Email Row */}
                  <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-6">
                    <div>
                      <label className="block text-sm md:text-base 2xl:text-lg text-black mb-2">
                        Designation <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleChange}
                        className={`w-full border-b border-gray-300 pb-2 focus:outline-none focus:border-blue-500 text-base md:text-lg 2xl:text-xl transition-colors ${
                          errors.designation ? 'border-red-500' : ''
                        }`}
                        placeholder="Enter your job title"
                        maxLength={100}
                        autoComplete="on"
                      />
                      {errors.designation && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.designation}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm md:text-base 2xl:text-lg text-black mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full border-b border-gray-300 pb-2 focus:outline-none focus:border-blue-500 text-base md:text-lg 2xl:text-xl transition-colors ${
                          errors.email ? 'border-red-500' : ''
                        }`}
                        placeholder="example@email.com"
                        maxLength={100}
                        autoComplete="off"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div className="mb-6">
                    <label className="block text-sm md:text-base 2xl:text-lg text-black mb-2">
                      Phone Number (with country code)
                    </label>
                    <input
                      type="text"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                      className={`w-full border-b border-gray-300 pb-2 focus:outline-none focus:border-blue-500 text-base md:text-lg 2xl:text-xl transition-colors ${
                        errors.phone_number ? 'border-red-500' : ''
                      }`}
                      placeholder="+91-9876543210"
                      maxLength={20}
                      autoComplete="off"
                    />
                    {errors.phone_number && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.phone_number}
                      </p>
                    )}
                  </div>

                  {/* Subject Selection */}
                  <div className="mb-8">
                    <label className="block text-sm md:text-base 2xl:text-lg font-poppins text-black mb-4">
                      Select Subject? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row justify-between gap-6 md:gap-4 lg:gap-6">
                      {subjectOptions.map((subject) => {
                        const isSelected = contact_us_subject === subject;

                        return (
                          <label
                            key={subject}
                            className="flex items-start gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              setContact_us_subject(subject);
                              if (errors.contact_us_subject) {
                                setErrors(prev => ({
                                  ...prev,
                                  contact_us_subject: undefined
                                }));
                              }
                            }}
                          >
                            <div
                              className={`w-4 h-4 md:w-5 md:h-3.5 2xl:w-6 2xl:h-4 rounded-full flex items-center justify-center transition-colors mt-1 ${
                                isSelected ? "bg-black" : "bg-gray-300"
                              }`}
                            >
                              {isSelected && (
                                <div className="w-2 h-2 md:w-2.5 md:h-2.5 2xl:w-3 2xl:h-3 bg-white rounded-full" />
                              )}
                            </div>
                            <span className="text-sm md:text-base 2xl:text-lg font-poppins text-black">
                              {subject}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {errors.contact_us_subject && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.contact_us_subject}
                      </p>
                    )}
                  </div>

                  {/* Message Field */}
                  <div className="mb-8">
                    <label className="block text-sm md:text-base 2xl:text-lg text-black mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="contact_us_message"
                      value={formData.contact_us_message}
                      onChange={handleChange}
                      className={`w-full border-b border-gray-300 pb-2 focus:outline-none focus:border-blue-500 min-h-[100px] text-base md:text-lg 2xl:text-xl transition-colors resize-none ${
                        errors.contact_us_message ? 'border-red-500' : ''
                      }`}
                      placeholder="Write your message here..."
                      maxLength={1000}
                      autoComplete="off"
                    />
                    {errors.contact_us_message && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.contact_us_message}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="text-right">
                    <button
                      type="submit"
                      className="bg-[#099CD6] text-white px-6 md:px-8 py-2 md:py-3 rounded-lg text-base md:text-lg 2xl:text-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Enhanced Consent Modal */}
          {showConsentModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-black">
                      Consent Required
                    </h2>
                    <button
                      onClick={() => setShowConsentModal(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      disabled={isSubmitting}
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <p className="text-gray-700 mb-6">
                    Please review and accept the following terms to proceed with your submission:
                  </p>

                  <div className="space-y-6">
                    {/* Consent 1 */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="commercial"
                        checked={consentCheckboxes.commercial}
                        onChange={() => handleConsentChange('commercial')}
                        className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="commercial" className="text-sm text-gray-700 cursor-pointer">
                        <strong>1.</strong> I consent to receiving commercial information from Ziegler via electronic means of communication within the meaning of the Act of 18 July 2002 on electronic services (Journal of Laws No 144, Item 1204, as amended).
                      </label>
                    </div>

                    {/* Consent 2 */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="marketing"
                        checked={consentCheckboxes.marketing}
                        onChange={() => handleConsentChange('marketing')}
                        className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="marketing" className="text-sm text-gray-700 cursor-pointer">
                        <strong>2.</strong> I agree to the processing of my personal data for marketing purposes in accordance with the Data Protection Policy, which I have read and accepted.
                      </label>
                    </div>

                    {/* Consent 3 */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="crm"
                        checked={consentCheckboxes.crm}
                        onChange={() => handleConsentChange('crm')}
                        className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="crm" className="text-sm text-gray-700 cursor-pointer">
                        <strong>3.</strong> I consent to the processing of my personal data, including my name, phone number, and email address, by Ziegler S.A., ul. Słubicka 4, 65-127 Zielona Góra, for contacting me via email or phone for marketing purposes. I have been informed that my personal data may be transferred to third countries outside the European Union. I have read the privacy policy and understand that my consent is voluntary and easy to withdraw. More information about my rights and data processing can be found in the Privacy Policy.
                      </label>
                    </div>

                    {/* Consent 4 */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="partnerZone"
                        checked={consentCheckboxes.partnerZone}
                        onChange={() => handleConsentChange('partnerZone')}
                        className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="partnerZone" className="text-sm text-gray-700 cursor-pointer">
                        <strong>4.</strong> Register account in the Partner Zone
                      </label>
                    </div>
                  </div>

                  {/* Modal Actions */}
                  <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
                    <button
                      onClick={() => setShowConsentModal(false)}
                      className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConsentSubmit}
                      disabled={isSubmitting || !Object.values(consentCheckboxes).every(consent => consent === true)}
                      className={`px-6 py-2 text-white rounded-lg transition-colors ${
                        Object.values(consentCheckboxes).every(consent => consent === true) && !isSubmitting
                          ? 'bg-[#099CD6] hover:bg-blue-600'
                          : 'bg-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isSubmitting ? 'Submitting...' : 'Accept & Submit'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ToastContainer 
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </div>
      )}
      <Footer />
    </>
  );
};

export default ContactUs;